/**
 * React Query mutation hook for creating ShowNotes blocks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import httpClient from 'src/utilities/http-client'

interface CreateBlockRequest {
  showNotesID: string
  type: 'text' | 'image' | 'pdf'
}

/**
 * Create a new block via API
 */
async function createBlock(
  request: CreateBlockRequest,
  basePath: string,
): Promise<void> {
  await httpClient.post(`/api/${basePath}/blocks`, request)
}

/**
 * Hook to create a new show notes block
 * Invalidates the blocks cache to refetch with new block and proper sequences
 */
export function useCreateBlock(
  showNotesID: string,
  basePath: string,
  onSuccess?: (type: 'text' | 'image' | 'pdf') => void,
) {
  let queryClient = useQueryClient()

  return useMutation({
    mutationFn: (type: 'text' | 'image' | 'pdf') =>
      createBlock({ showNotesID, type }, basePath),
    onSuccess: (_, type) => {
      queryClient.invalidateQueries({
        queryKey: ['showNotesBlocks', basePath, showNotesID],
      })
      queryClient.refetchQueries({
        queryKey: ['showNotesBlocks', basePath, showNotesID],
        type: 'active',
      })
      onSuccess?.(type)
    },
    onError: () => {
      // Error handling can be implemented here if needed
    },
  })
}
